<template>
  <main>
    <AnalyticsControlsBar
      class="display-wrapper"
      :spin="reloadButtonNeedsToSpin"
      :range="currentRange"
      :process-group-filter="activeTab === 'processes' ? processGroupFilter : null"
      :queue-group-filter="activeTab === 'queues' ? queueGroupFilter : null"
      @updateData="handleRangeChange"
      @update:processExcluded="handleProcessExcluded"
      @update:queueExcluded="handleQueueExcluded"
      @databaseChange="handleDatabaseChange"
    />

    <TabView :tabs="tabs" v-model:active-tab="activeTab">
      <template #queues>
        <div class="tab-rows">
        <div class="split-row row">
          <StatsGrid class="display-wrapper" ref="queueStatsGrid" :count="4" />
          <BaseChart
            ref="queueCombinedBarChart"
            class="box-shadow display-wrapper"
            chart-type="bar"
            title="Cost and Time Savings"
            :subtitle="annotateSubtitle('Comparison of Monthly Monetary and Time Savings', currentRange, todayIso)"
            label-y-axis="DKK"
            label-y-axis-secondary="Hours (h)"
            :secondary-axis-series="['Saved Hours']"
            :data-processor="combinedBarProcessor"
            :color-map="combinedColorMap"
          />
        </div>
        <div class="equal-row row">
          <SortedBarChart
            ref="queueSavingsChart"
            class="box-shadow display-wrapper"
            title="Total cost saved"
            :subtitle="annotateSubtitle('Amount saved by queue (DKK)', currentRange, todayIso)"
            label-y-axis="DKK"
            :color-map="moneyColorMap"
            :sorting-fn="sortByRoiCostSavedDesc"
            :label-extractor="extractRoiDisplayName"
            :value-extractor="extractRoiCostSaved"
            :horizontal="true"
          />
          <FilterTableViewer
            ref="queueTableViewer"
            class="box-shadow display-wrapper"
            search-on-column="name"
            :scrollable="true"
            :disable-shadow="true"
            :disable-column-dropdown="true"
          />
        </div>
        </div>
      </template>

      <template #processes>
        <div class="tab-rows">
        <div class="split-row row">
          <StatsGrid class="display-wrapper" ref="processStatsGrid" :count="4" />
          <BaseChart
            ref="processCombinedBarChart"
            class="box-shadow display-wrapper"
            chart-type="bar"
            title="Cost and Time Savings"
            :subtitle="annotateSubtitle('Comparison of Monthly Monetary and Time Savings', currentRange, todayIso)"
            label-y-axis="DKK"
            label-y-axis-secondary="Hours (h)"
            :secondary-axis-series="['Saved Hours']"
            :data-processor="combinedBarProcessor"
            :color-map="combinedColorMap"
          />
        </div>
        <div class="equal-row row">
          <SortedBarChart
            ref="processSavingsChart"
            class="box-shadow display-wrapper"
            title="Total cost saved"
            :subtitle="annotateSubtitle('Amount saved by process (DKK)', currentRange, todayIso)"
            label-y-axis="DKK"
            :color-map="moneyColorMap"
            :sorting-fn="sortByRoiCostSavedDesc"
            :label-extractor="extractRoiDisplayName"
            :value-extractor="extractRoiCostSaved"
            :horizontal="true"
          />
          <FilterTableViewer
            ref="processTableViewer"
            class="box-shadow display-wrapper"
            search-on-column="name"
            :scrollable="true"
            :disable-shadow="true"
            :disable-column-dropdown="true"
          />
        </div>
        </div>
      </template>
    </TabView>
  </main>
</template>

<script setup>
import { onMounted, onActivated, computed, ref, watch } from 'vue'
import { useRoiRefreshStore } from '@/stores/roiRefreshStore'
import { useDateRangeStore } from '@/stores/useDateRangeStore'
import { useGroupFilterStore } from '@/stores/useGroupFilterStore'
import { useDatabaseStore } from '@/stores/useDatabaseStore'
import { useGroupFilteredData, GROUP_FILTER_EMPTY_MESSAGE } from '@/Analytics/composables/useGroupFilteredData'

import AnalyticsControlsBar from '@/Analytics/components/AnalyticsControlsBar.vue'
import StatsGrid from '@/Analytics/components/StatsGrid.vue'
import SortedBarChart from '@/Analytics/components/NewGraphs/Reusable/SortedBarChart.vue'
import BaseChart from '@/components/Graph/BaseChart.vue'
import FilterTableViewer from '@/components/Automation/FilterTableViewer.vue'
import TabView from '@/components/TabView.vue'

import { combinedBarProcessor } from '@/Modules/Grafer/dataProcessor/combinedMoneyHoursDataProcessor'
import { getQueueStats, getJobStats } from '@/Services/Ressources/API/OperationelService'
import { getAllProcessRoiStats, getAllQueueRoiStats } from '@/Services/Ressources/API/ForretningService'
import { formatNumber } from '@/Modules/utils/useFormatNumber'
import { calculateRoiStats } from '@/Modules/utils/roiStats'
import { annotateSubtitle, toIsoDate } from '@/Modules/utils/dateRange'
import { useRangeGraphUpdater, wrapRequestInRange } from '@/Modules/Grafer/common/useUpdateData'

const todayIso = toIsoDate(new Date())

const tabs = [
  { id: 'processes', label: 'Processes' },
  { id: 'queues', label: 'Queues' }
]

const activeTab = ref('processes')

const combinedColorMap = {
  'Saved DKK': '#2ecc71',
  'Saved Hours': '#3498db'
}

const moneyColorMap = {
  'Saved DKK': '#2ecc71'
}

const perJobTimeSavedHours = (p) => (p.manualTimeMinutes || 0) / 60
const perJobCostSaved = (p) => perJobTimeSavedHours(p) * (p.hourlyCost || 0)
const itemTotalCostSaved = (p) => (p.transactions || 0) * perJobCostSaved(p)

const sortByRoiCostSavedDesc = (a, b) => itemTotalCostSaved(b) - itemTotalCostSaved(a)
const extractRoiDisplayName = item => item.displayName || item.processName || item.queueName
const extractRoiCostSaved = item => +itemTotalCostSaved(item).toFixed(2)

const processTableDataMapper = (items) => items.map(p => ({
  name: p.displayName || p.processName,
  jobs: formatNumber(p.transactions),
  'time / job (h)': formatNumber(perJobTimeSavedHours(p)),
  'cost / job (DKK)': formatNumber(perJobCostSaved(p))
}))

const queueTableDataMapper = (items) => items.map(p => ({
  name: p.displayName || p.queueName,
  transactions: formatNumber(p.transactions),
  'time / transaction (h)': formatNumber(perJobTimeSavedHours(p)),
  'cost / transaction (DKK)': formatNumber(perJobCostSaved(p))
}))

const databaseStore = useDatabaseStore()

const fetchData = async (range, forceUpdate) => {
  const db = databaseStore.selected
  const [queueStats, processStats, queueRoiStats, jobStats] = await Promise.all([
    wrapRequestInRange(getQueueStats, range.from, range.to, forceUpdate, db),
    wrapRequestInRange(getAllProcessRoiStats, range.from, range.to, forceUpdate, db),
    wrapRequestInRange(getAllQueueRoiStats, range.from, range.to, forceUpdate, db),
    wrapRequestInRange(getJobStats, range.from, range.to, forceUpdate, db),
  ])

  const result = { queueStats, queueRoiStats: queueRoiStats || [], processStats: processStats || [], jobStats, range }
  processStatsRef.value = result.processStats
  queueRoiStatsRef.value = result.queueRoiStats
  jobStatsRef.value = jobStats || []
  queueStatsRef.value = queueStats || []
  return result
}

const populateMap = {}

const dateRangeStore = useDateRangeStore()
const currentRange = computed(() => dateRangeStore.getRange('business'))

const emptyStateOptions = (filtered, raw) =>
  filtered.length === 0 && raw.length > 0 ? { emptyMessage: GROUP_FILTER_EMPTY_MESSAGE } : {}

const processStatsRef = ref([])
const jobStatsRef = ref([])
const queueStatsRef = ref([])
const groupFilterStore = useGroupFilterStore()
const processExcluded = computed(() => groupFilterStore.getExcluded('business', 'process'))
const processHasUngrouped = computed(() => processStatsRef.value.some((r) => r.groupId == null))

const { filteredData: filteredProcessStats, groups: processGroups } = useGroupFilteredData({
  data: processStatsRef,
  excluded: processExcluded,
  extractGroupId: (row) => row.groupId,
  extractGroupName: (row) => row.groupName,
  hasUngrouped: processHasUngrouped
})

const processGroupFilter = computed(() => ({
  groups: processGroups.value,
  excluded: processExcluded.value,
  label: 'Process groups',
  hasUngrouped: processHasUngrouped.value
}))

const handleProcessExcluded = (next) => {
  groupFilterStore.setExcluded('business', 'process', next)
}

const processTableViewer = ref(null)
const processStatsGrid = ref(null)
const processSavingsChart = ref(null)
watch(
  filteredProcessStats,
  (rows) => {
    const opts = emptyStateOptions(rows, processStatsRef.value)
    processTableViewer.value?.populateComponent(processTableDataMapper(rows), opts)
    processStatsGrid.value?.populateComponent(calculateRoiStats(rows, currentRange.value, 'Jobs'), opts)
    processSavingsChart.value?.populateComponent(rows, opts)
  },
  { flush: 'post' }
)

// The combined monthly chart is fed by jobStats (operational), which carry no
// group id — map releaseName -> group via the ROI rows so it filters in step
// with the process dropdown.
const processRoiGroupMap = computed(() => {
  const map = new Map()
  for (const row of processStatsRef.value) {
    if (row.processName != null && row.groupId != null) {
      map.set(row.processName, { groupId: row.groupId, groupName: row.groupName })
    }
  }
  return map
})

const { filteredData: filteredJobStats } = useGroupFilteredData({
  data: jobStatsRef,
  excluded: processExcluded,
  extractGroupId: (job) => processRoiGroupMap.value.get(job.releaseName)?.groupId ?? null,
  extractGroupName: (job) => processRoiGroupMap.value.get(job.releaseName)?.groupName,
  hasUngrouped: processHasUngrouped
})

const processCombinedBarChart = ref(null)
watch(
  filteredJobStats,
  (jobs) => {
    const opts = emptyStateOptions(jobs, jobStatsRef.value)
    processCombinedBarChart.value?.populateComponent(jobs, opts)
  },
  { flush: 'post' }
)

const queueRoiStatsRef = ref([])
const queueExcluded = computed(() => groupFilterStore.getExcluded('business', 'queue'))
const queueHasUngrouped = computed(() => queueRoiStatsRef.value.some((r) => r.groupId == null))

const { filteredData: filteredQueueStats, groups: queueGroups } = useGroupFilteredData({
  data: queueRoiStatsRef,
  excluded: queueExcluded,
  extractGroupId: (row) => row.groupId,
  extractGroupName: (row) => row.groupName,
  hasUngrouped: queueHasUngrouped
})

const queueGroupFilter = computed(() => ({
  groups: queueGroups.value,
  excluded: queueExcluded.value,
  label: 'Queue groups',
  hasUngrouped: queueHasUngrouped.value
}))

const handleQueueExcluded = (next) => {
  groupFilterStore.setExcluded('business', 'queue', next)
}

const queueTableViewer = ref(null)
const queueStatsGrid = ref(null)
const queueSavingsChart = ref(null)
watch(
  filteredQueueStats,
  (rows) => {
    const opts = emptyStateOptions(rows, queueRoiStatsRef.value)
    queueTableViewer.value?.populateComponent(queueTableDataMapper(rows), opts)
    queueStatsGrid.value?.populateComponent(calculateRoiStats(rows, currentRange.value, 'Transactions'), opts)
    queueSavingsChart.value?.populateComponent(rows, opts)
  },
  { flush: 'post' }
)

const queueRoiGroupMap = computed(() => {
  const map = new Map()
  for (const row of queueRoiStatsRef.value) {
    if (row.queueName != null && row.groupId != null) {
      map.set(row.queueName, { groupId: row.groupId, groupName: row.groupName })
    }
  }
  return map
})

const { filteredData: filteredQueueOpStats } = useGroupFilteredData({
  data: queueStatsRef,
  excluded: queueExcluded,
  extractGroupId: (q) => queueRoiGroupMap.value.get(q.queueDefinitionName)?.groupId ?? null,
  extractGroupName: (q) => queueRoiGroupMap.value.get(q.queueDefinitionName)?.groupName,
  hasUngrouped: queueHasUngrouped
})

const queueCombinedBarChart = ref(null)
watch(
  filteredQueueOpStats,
  (queues) => {
    const opts = emptyStateOptions(queues, queueStatsRef.value)
    queueCombinedBarChart.value?.populateComponent(queues, opts)
  },
  { flush: 'post' }
)

const { updateData, reloadButtonNeedsToSpin } = useRangeGraphUpdater({
  fetchData,
  populateMap
})

const handleRangeChange = (range) => {
  dateRangeStore.setRange('business', range)
  updateData(range)
}

// Switching the active database re-queries the current range against the new DB.
const handleDatabaseChange = () => {
  updateData(dateRangeStore.getRange('business'))
}

const roiRefreshStore = useRoiRefreshStore()

const handleActivation = async () => {
  await databaseStore.init()
  roiRefreshStore.clearDirty()
  updateData(dateRangeStore.getRange('business'))
}

onMounted(handleActivation)
onActivated(handleActivation)
</script>

<style lang="css">
.box-shadow {
  border: var(--border-card);
  box-shadow: var(--shadow-card);
}

.box-shadow:hover {
  box-shadow: var(--shadow-card-hover);
  transform: translateY(-2px);
}

.display-wrapper {
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border-radius: var(--border-radius-card);
  margin-right: 20px;
}

main {
  display: grid;
  gap: 16px;
  padding: 16px;
}

.tab-rows {
  display: grid;
  gap: 16px;
}

.split-row {
  grid-template-columns: 1.5fr 2.5fr;
}

.equal-row {
  grid-template-columns: 1fr 1fr;
}

.row {
  display: grid;
  gap: 5px;
  align-items: stretch;
}

main section {
  padding: 1rem 0.5rem;
}
</style>